import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, homePath, allowedPaths } from "@/context/AuthContext";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UploadData from "@/pages/UploadData";
import Poliklinik from "@/pages/Poliklinik";
import TargetTahunan from "@/pages/TargetTahunan";
import RekapDepartemen from "@/pages/RekapDepartemen";
import RekapBulan from "@/pages/RekapBulan";
import DashboardDepartemen from "@/pages/DashboardDepartemen";
import Ringkasan from "@/pages/Ringkasan";
import RiwayatUpload from "@/pages/RiwayatUpload";
import Notifikasi from "@/pages/Notifikasi";
import Export from "@/pages/Export";
import GantiPassword from "@/pages/GantiPassword";
import Users from "@/pages/Users";
import LaporSiport from "@/pages/LaporSiport";
import SiportDashboard from "@/pages/siport/SiportDashboard";
import SiportData from "@/pages/siport/SiportData";
import SiportMaster from "@/pages/siport/SiportMaster";
import SiportPengaturan from "@/pages/siport/SiportPengaturan";
import SiportImport from "@/pages/siport/SiportImport";

function NoAccess({ to }) {
  useEffect(() => {
    toast.error("Tidak punya akses ke halaman tersebut", { id: "no-access" });
  }, []);
  return <Navigate to={to} replace />;
}

function Protected({ children, requireSuper }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const allowed = allowedPaths(user);
  if (allowed && !allowed.includes(pathname)) return <NoAccess to={homePath(user)} />;
  if (requireSuper && user.role !== "super_admin") return <NoAccess to={homePath(user)} />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  return <Navigate to={user ? homePath(user) : "/login"} replace />;
}

const routes = [
  ["/dashboard", <Dashboard />],
  ["/upload", <UploadData />],
  ["/poliklinik", <Poliklinik />],
  ["/target", <TargetTahunan />],
  ["/rekap-departemen", <RekapDepartemen />],
  ["/rekap-bulan", <RekapBulan />],
  ["/dashboard-departemen", <DashboardDepartemen />],
  ["/ringkasan", <Ringkasan />],
  ["/riwayat", <RiwayatUpload />],
  ["/notifikasi", <Notifikasi />],
  ["/export", <Export />],
  ["/siport/dashboard", <SiportDashboard />],
  ["/siport/data", <SiportData />],
  ["/siport/master", <SiportMaster />],
  ["/siport/pengaturan", <SiportPengaturan />],
  ["/siport/import", <SiportImport />],
  ["/users", <Users />, true],
  ["/ganti-password", <GantiPassword />],
];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/lapor-siport" element={<LaporSiport />} />
            <Route path="/" element={<HomeRedirect />} />
            {routes.map(([path, el, requireSuper]) => (
              <Route key={path} path={path} element={<Protected requireSuper={requireSuper}>{el}</Protected>} />
            ))}
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
